/**
 * ContactButton Component
 * 
 * This component renders a button that opens a contact modal for messaging a VA.
 * It handles conversation creation and provides a smooth messaging experience.
 */

import React, { useState } from 'react';
import { MessageCircle, Send, Mail } from 'lucide-react';
import { conversationService } from '../../services/conversationService';
import { useAuth } from '../../hooks/useAuth';
import ContactModal from './ContactModal';

const ContactButton = ({ 
  vaId, 
  vaName, 
  jobId = null,
  variant = 'primary', 
  size = 'sm', 
  showIcon = true,
  fullWidth = false,
  className = '',
  onMessageSent = null // Callback for when message is sent
}) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);

  // Button styling variants
  const variants = {
    primary: 'bg-green-600 hover:bg-green-700 text-white',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300',
    outline: 'border border-green-600 text-green-600 hover:bg-green-50',
    ghost: 'text-green-600 hover:bg-green-50',
    link: 'text-green-600 hover:text-green-800 underline-offset-4 hover:underline'
  };

  const sizes = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  const widthClass = fullWidth ? 'w-full' : '';

  const handleContactClick = async () => {
    if (!vaId) {
      console.error('VA ID is required to initiate contact');
      return;
    }

    if (!user?.id) {
      console.error('User must be authenticated to contact VAs');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create or get existing conversation
      const conversation = await conversationService.createOrGetConversation(
        user.id,
        vaId,
        jobId
      );
      
      setConversationId(conversation.id);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError(err.message || 'Failed to start conversation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setConversationId(null);
    setError(null);
  };

  const handleMessageSent = (messageData) => {
    // Call parent callback if provided
    if (onMessageSent) {
      onMessageSent(messageData);
    }
    
    // Close modal after successful message
    handleCloseModal();
  };

  // Determine button content based on variant
  const isLinkVariant = variant === 'link';
  const baseClasses = isLinkVariant 
    ? 'inline-flex items-center gap-1 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
    : `inline-flex items-center gap-1 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${sizes[size]}`;

  const buttonClasses = `${baseClasses} ${variants[variant]} ${widthClass} ${className}`;

  return (
    <>
      <button
        onClick={handleContactClick}
        disabled={isLoading}
        className={buttonClasses}
        aria-label={`Contact ${vaName}`}
        title={`Send a message to ${vaName}`}
      >
        {isLoading ? (
          <>
            <div className={`animate-spin rounded-full border-2 border-current border-t-transparent ${iconSizes[size]}`} />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {showIcon && (
              <MessageCircle className={iconSizes[size]} aria-hidden="true" />
            )}
            <span>Contact</span>
            {isLinkVariant && (
              <Send className="w-3 h-3" aria-hidden="true" />
            )}
          </>
        )}
      </button>

      {/* Error Display */}
      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          <p className="font-medium">Error starting conversation:</p>
          <p>{error}</p>
        </div>
      )}

      {/* Contact Modal */}
      {isModalOpen && conversationId && (
        <ContactModal
          vaId={vaId}
          vaName={vaName}
          conversationId={conversationId}
          jobId={jobId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onMessageSent={handleMessageSent}
        />
      )}
    </>
  );
};

export default ContactButton;

